/**
 * The caller's site (name, timezone, work hours) — `GET /api/settings`.
 * Shares the `["settings"]` cache with the Settings page, so a rename there
 * relabels the sidebar immediately.
 */
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

export interface SiteSettings {
  site_id: string;
  name: string;
  name_he?: string | null;
  timezone: string;
  work_hours_start: string;
  work_hours_end: string;
  work_days: number[];
  /**
   * Escalation (2026-09 API contract): who gets a push when a FALL / FIRE /
   * emergency alert is still unhandled after `escalation_minutes`. Both are
   * ABSENT on a server that predates the contract — the Settings page hides
   * its escalation block then — and `escalation_user_id` is null when no one
   * is set.
   */
  escalation_user_id?: string | null;
  escalation_minutes?: number;
}

export function useSite() {
  const user = useAuth((s) => s.user);
  return useQuery<SiteSettings>({
    queryKey: ["settings"],
    queryFn: () => api<SiteSettings>("/api/settings"),
    enabled: Boolean(user?.site_id),
    staleTime: 5 * 60_000,
  });
}

/** Display name for the site in the active language (Hebrew name when the UI
 *  is Hebrew and one is set). */
export function siteDisplayName(site: SiteSettings | undefined, lang: string): string {
  if (!site) return "";
  return (lang === "he" && site.name_he) || site.name;
}
